import { Card } from "@/components/Card";
import { useTracking } from "@/requests/hooks/app";
import { faLocationDot } from "@fortawesome/pro-duotone-svg-icons";
import {
  faCaretLeft,
  faCaretRight,
  faCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { tv } from "tailwind-variants";

const styles = tv({
  slots: {
    base: "flex flex-col gap-20",
    imagePaddingContainer: "p-10 md:p-40",
    imageContainer: "flex items-center justify-center md:min-h-[361px]",
    image: "h-auto w-full md:h-full md:min-h-full md:w-[unset]",
    leadingIconAndText: "flex items-center gap-10",
    actionsSection:
      "flex flex-row items-start justify-end gap-30 md:justify-between",
    thumbnailSection: "hidden flex-row gap-20 md:flex",
    thumbnailImage: "flex h-full items-center justify-center",
    ctaContainer: "flex flex-row items-center gap-20",
    toggleButtonContainer:
      "min-w-[50px] shrink-0 cursor-pointer rounded-full border-none bg-gradient-to-r from-primary to-green p-[1px]",
    toggleIconContainer:
      "flex h-50 w-50 items-center justify-center rounded-full",
    toggleIcon:
      "flex h-50 w-50 items-center justify-center rounded-full bg-white",
    dotsContainer: "flex flex-row gap-10",
  },
  variants: {
    hiddenPreview: {
      true: {
        imagePaddingContainer: "p-10 md:p-10",
        ctaContainer: "flex w-full flex-row items-center justify-center gap-50",
      },
    },
  },
});

export interface ITrailingContent {
  title: string;
  description: string;
}

export interface IImagePreviewItem {
  title: string | JSX.Element;
  image: string;
  trailingContent?: ITrailingContent;
}

interface IProps {
  images: IImagePreviewItem[];
  imageTrailingContent?: string | JSX.Element;
  hidePreviews?: boolean;
  identifier?: string;
  setTrailingContent?: (trailingContent?: ITrailingContent) => void;
}

const ImagePreviewCarousel = ({
  images,
  identifier,
  imageTrailingContent,
  hidePreviews,
  setTrailingContent,
}: IProps) => {
  const {
    base,
    imagePaddingContainer,
    imageContainer,
    leadingIconAndText,
    image,
    actionsSection,
    thumbnailSection,
    thumbnailImage,
    ctaContainer,
    toggleButtonContainer,
    toggleIconContainer,
    toggleIcon,
    dotsContainer,
  } = styles({ hiddenPreview: hidePreviews });
  const [selectedImage, setSelectedImage] = useState<IImagePreviewItem>(
    images[0],
  );
  const { mutate: sendTracking } = useTracking();

  useEffect(() => {
    setSelectedImage(images[0]);
  }, [images]);

  const getCurrentIndex = () =>
    images.findIndex(({ image }) => image === selectedImage.image);

  const selectImage = (index: number) => {
    setSelectedImage(images[index]);
    setTrailingContent?.(images[index]?.trailingContent);
  };

  const handleThumbnailClick = (index: number) => () => {
    selectImage(index);
  };

  const handlePreviousClick = () => {
    sendTracking({
      eventName: "component.image-preview-carousel.previous",
      additionalProperties: {
        carousel: identifier,
        active_image: selectedImage.image,
      },
    });

    const currentIndex = getCurrentIndex();

    if (currentIndex === 0) {
      selectImage(images.length - 1);
    } else {
      selectImage(currentIndex - 1);
    }
  };

  const handleNextClick = () => {
    sendTracking({
      eventName: "component.image-preview-carousel.next",
      additionalProperties: {
        carousel: identifier,
        active_image: selectedImage.image,
      },
    });

    const currentIndex = getCurrentIndex();

    if (currentIndex === images.length - 1) {
      selectImage(0);
      return;
    }

    selectImage(currentIndex + 1);
  };

  const renderImageSection = () => (
    <Card rounded="md" backgroundColor="gray" shadow={false}>
      <div className={imagePaddingContainer()}>
        <div className={imageContainer()}>
          <img className={image()} src={selectedImage.image} />
        </div>
      </div>
      {!!imageTrailingContent && imageTrailingContent}
    </Card>
  );

  const renderLeadingIconAndText = () => (
    <div className={leadingIconAndText()}>
      <FontAwesomeIcon icon={faLocationDot} className="h-20 text-primary" />
      {selectedImage.title}
    </div>
  );

  const renderThumbnailsSection = () => {
    if (hidePreviews) return null;

    return (
      <div className={thumbnailSection()} data-testid="thumbnails">
        {images.map(({ image }, i) => (
          <Card
            key={i}
            additionalClassName="w-[156px] p-20"
            rounded="sm"
            onClick={handleThumbnailClick(i)}>
            <div className={thumbnailImage()}>
              <img src={image} />
            </div>
          </Card>
        ))}
      </div>
    );
  };

  const renderDots = () =>
    images.map((_, i) => (
      <button
        key={i}
        className="cursor-pointer"
        onClick={handleThumbnailClick(i)}>
        <FontAwesomeIcon
          icon={faCircle}
          className={
            i === getCurrentIndex()
              ? "h-10 w-10 text-secondary"
              : "h-10 w-10 text-primary-light"
          }
        />
      </button>
    ));

  const renderCTAs = (hideDots: boolean = false) => (
    <div className={ctaContainer()}>
      <button
        className={toggleButtonContainer()}
        onClick={handlePreviousClick}
        data-testid="image-preview-previous">
        <div className={toggleIconContainer()}>
          <div className={toggleIcon()}>
            <FontAwesomeIcon icon={faCaretLeft} className="h-15" />
          </div>
        </div>
      </button>

      {hideDots && (
        <div data-testid="image-carousel-dots" className={dotsContainer()}>
          {renderDots()}
        </div>
      )}

      <button
        className={toggleButtonContainer()}
        onClick={handleNextClick}
        data-testid="image-preview-next">
        <div className={toggleIconContainer()}>
          <div className={toggleIcon()}>
            <FontAwesomeIcon icon={faCaretRight} className="h-15" />
          </div>
        </div>
      </button>
    </div>
  );

  return (
    <div className={base()}>
      {renderLeadingIconAndText()}
      {renderImageSection()}
      <div className={actionsSection()}>
        {renderThumbnailsSection()}
        {renderCTAs(!!hidePreviews)}
      </div>
    </div>
  );
};

export default ImagePreviewCarousel;
